import get from "lodash/get";
import {useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";

import {FormLabel} from "@/components/form/FormLabel.tsx";
import {FormSubtext} from "@/components/formSubtext/FormSubtext.tsx";
import {Input} from "@/components/input/Input.tsx";
import {Select} from "@/components/select/Select.tsx";
import {LockedDropdownIndicator} from "@/components/select/subComponents/DropdownIndicator/DropdownIndicator.tsx";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {isNumeric} from "@/lib/validators.tsx";
import {
  filterOutIntervalOptions,
  findIntervalOption,
  findIntervalSuffix,
  intervalMinutesFromDisplay,
  IntervalOption,
  intervalOptionByMinute,
  IntervalOptions,
  intervalValueDisplay,
} from "@/pages/checks/components/form/fields/interval/utils.ts";

import styles from "./IntervalInputGroupField.module.scss";

interface IntervalInputGroupFieldProps {
  className?: string;
  intervalLabel?: string;
  name: string;
  min: number;
  max: number;
  lockedTooltip?: string;
}

export const IntervalInputGroupField = ({
  className,
  name,
  intervalLabel,
  min,
  max,
  lockedTooltip,
}: IntervalInputGroupFieldProps) => {
  const {
    watch,
    getFieldState,
    setValue,
    setError,
    clearErrors,
    formState: {errors, defaultValues},
  } = useFormContext();
  const value = watch(name);
  const {isDirty} = getFieldState(name);
  const defaultValue = get(defaultValues, name);
  const options = filterOutIntervalOptions(IntervalOptions, max);
  const isSelectDisabled = options.length < 2;
  const forceOption = isSelectDisabled ? (options[0].value as IntervalOption) : undefined;
  const [selectValue, setSelectValue] = useState(findIntervalOption(value, forceOption));
  const selectedIntervalType = selectValue?.value as IntervalOption;
  const innerValue = intervalValueDisplay(value, selectedIntervalType);
  const valueError = errors[name];
  const minValueDisplay = intervalValueDisplay(min, intervalOptionByMinute(min));
  const minInterval = findIntervalOption(min);
  const maxValueDisplay = intervalValueDisplay(max, intervalOptionByMinute(max));
  const maxInterval = findIntervalOption(max);

  useEffect(() => {
    // catch up with default field update. Happens if user switches check type and form renders twice
    // first run - current values, second run - new default values
    if (!defaultValue || isDirty) {
      return;
    }
    setSelectValue(findIntervalOption(defaultValue, forceOption));
  }, [defaultValue, forceOption, isDirty]);

  const setInterval = (input: number, intervalType: IntervalOption) => {
    let hasError = false;
    const newValue = intervalMinutesFromDisplay(input, intervalType);
    if (newValue < min) {
      setError(name, {
        message: `Interval must be greater than or equal to ${findIntervalSuffix(
          minValueDisplay,
          minInterval!.value as IntervalOption,
          true
        )}`,
      });
      hasError = true;
    }
    if (newValue > max) {
      setError(name, {
        message: `Interval must be less than or equal to ${findIntervalSuffix(
          maxValueDisplay,
          maxInterval!.value as IntervalOption,
          true
        )}`,
      });
      hasError = true;
    }

    setValue(name, newValue, {shouldDirty: true});
    if (!hasError) {
      clearErrors([name]);
    }
    return newValue;
  };

  const handleSelectChange = (v: unknown) => {
    if (!v || typeof v !== "object" || !("value" in v)) {
      return;
    }
    const newValue = setInterval(innerValue, v.value as IntervalOption);
    setSelectValue(findIntervalOption(newValue));
  };

  return (
    <div className={className}>
      <FormLabel forId="name" required>
        {intervalLabel || "Check Interval: "}
      </FormLabel>
      <div className="d-flex gap-3">
        <Input
          className="pulseMaxWidthXXS"
          aria-invalid={!!valueError}
          name={name}
          value={isNaN(innerValue) ? "" : innerValue}
          onChange={(v) => {
            // no Yup, manual validation 🤷
            const input = v.target.value;
            const parsedInput = parseInt(input);
            if (!input || !isNumeric(input)) {
              setValue(name, intervalMinutesFromDisplay(parsedInput, selectedIntervalType), {
                shouldDirty: true,
              });
              setSelectValue(findIntervalOption(0, selectedIntervalType));
              setError(name, {
                message: `Interval must be greater than or equal to ${findIntervalSuffix(
                  minValueDisplay,
                  minInterval!.value as IntervalOption,
                  true
                )}`,
              });
              return;
            }

            const newValue = setInterval(parsedInput, selectedIntervalType);
            setSelectValue(findIntervalOption(newValue, selectedIntervalType));
          }}
          type="number"
        />
        <Tooltip tooltipContent={isSelectDisabled ? lockedTooltip : undefined} onModal>
          <div aria-label={isSelectDisabled ? "Locked field" : undefined}>
            <Select
              className={styles.unitSelect}
              defaultValue={selectValue}
              value={selectValue}
              onChange={handleSelectChange}
              isClearable={false}
              isDisabled={isSelectDisabled}
              components={
                isSelectDisabled
                  ? {DropdownIndicator: LockedDropdownIndicator, IndicatorSeparator: () => null}
                  : undefined
              }
              options={options}
            />
          </div>
        </Tooltip>
      </div>
      <FormSubtext error={valueError?.message?.toString()} />
    </div>
  );
};
